import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { 
  FiActivity,
  FiDroplet,
  FiHeart, 
  FiMoon, 
  FiZap, 
  FiTrendingUp, 
  FiTrendingDown, 
  FiInfo 
} from 'react-icons/fi';
import { TbBrain, TbWalk } from 'react-icons/tb';

const METRIC_CONFIGS = {
  StressAvgAVG: {
    label: "Calma (Estrés)",
    unit: "/100",
    min: 0,
    max: 100,
    invert: true,
    color: "#EF4444", // red-500
    bgColor: "rgba(239, 68, 68, 0.08)",
    chartColor: "#F87171", // red-400
  },
  HRVValueAvgAVG: {
    label: "Variabilidad Cardíaca",
    unit: " ms",
    min: 0,
    max: 100,
    invert: false,
    color: "#3B82F6", // blue-500
    bgColor: "rgba(59, 130, 246, 0.08)",
    chartColor: "#60A5FA", // blue-400
  },
  SleepHoursAVG: {
    label: "Horas de Sueño",
    unit: " hrs",
    min: 0,
    max: 8.5,
    invert: false,
    color: "#6366F1", // indigo-500
    bgColor: "rgba(99, 102, 241, 0.08)",
    chartColor: "#818CF8", // indigo-400
  },
  StepsAVG: {
    label: "Pasos Promedio",
    unit: " pasos",
    min: 0,
    max: 10000,
    invert: false,
    color: "#10B981", // emerald-500
    bgColor: "rgba(16, 185, 129, 0.08)",
    chartColor: "#34D399", // emerald-400
  },
  CaloriesAVG: {
    label: "Calorías Activas",
    unit: " kcal",
    min: 0,
    max: 400,
    invert: false,
    color: "#F59E0B", // amber-500
    bgColor: "rgba(245, 158, 11, 0.08)",
    chartColor: "#FBBF24", // amber-400
  },
  GlucoseAVG: {
    label: "Glucosa",
    unit: "",
    min: 0,
    max: 300,
    invert: false,
    color: "#06B6D4", // cyan-500
    bgColor: "rgba(6, 182, 212, 0.08)",
    chartColor: "#22D3EE", // cyan-400
  },
  BloodPressureAVG: {
    label: "PA",
    unit: "",
    min: 0,
    max: 200,
    invert: false,
    color: "#EC4899", // pink-500
    bgColor: "rgba(236, 72, 153, 0.08)",
    chartColor: "#F472B6", // pink-400
  }
};

const getNormalizedValue = (value, config, actualMax) => {
  if (value === undefined || value === null || isNaN(Number(value))) return null;
  const { min, invert } = config;
  const max = actualMax || config.max;
  const clamped = Math.min(max, Math.max(min, Number(value)));
  const range = max - min || 1;
  let percent = ((clamped - min) / range) * 100;
  if (invert) {
    percent = 100 - percent;
  }
  return Math.round(percent);
};

const formatValue = (val, key) => {
  if (val === undefined || val === null || isNaN(Number(val))) return "0";
  if (key === 'StepsAVG') {
    return Math.round(val).toLocaleString();
  }
  if (key === 'CaloriesAVG' || key === 'StressAvgAVG' || key === 'HRVValueAvgAVG') {
    return Math.round(val).toString();
  }
  return Number(val).toFixed(1);
};

const calculateKPIs = (data, key, config) => {
  if (!data || data.length === 0) {
    return { avg: 0, trend: 0, trendDirection: 'none' };
  }

  const values = data
    .map(d => d[key])
    .filter(v => v !== undefined && v !== null && !isNaN(Number(v)))
    .map(Number);

  if (values.length === 0) {
    return { avg: 0, trend: 0, trendDirection: 'none' };
  }

  const total = values.reduce((sum, v) => sum + v, 0);
  const avg = total / values.length;

  if (values.length < 2) {
    return { avg, trend: 0, trendDirection: 'none' };
  }

  const mid = Math.ceil(values.length / 2);
  const firstHalf = values.slice(0, mid);
  const secondHalf = values.slice(mid);

  const avg1 = firstHalf.reduce((sum, v) => sum + v, 0) / firstHalf.length;
  const avg2 = secondHalf.reduce((sum, v) => sum + v, 0) / secondHalf.length;

  let trend = 0;
  if (avg1 !== 0) {
    trend = ((avg2 - avg1) / avg1) * 100;
  } else if (avg2 !== 0) {
    trend = 100;
  }

  let trendDirection = 'none';
  if (trend > 0.1) {
    trendDirection = config.invert ? 'worse' : 'better';
  } else if (trend < -0.1) {
    trendDirection = config.invert ? 'better' : 'worse';
  }

  return { avg, trend, trendDirection };
};

const getIcon = (key, color) => {
  const className = "w-4 h-4";
  switch (key) {
    case 'StressAvgAVG':
      return <TbBrain className={className} style={{ color }} />;
    case 'HRVValueAvgAVG':
      return <FiHeart className={className} style={{ color }} />;
    case 'SleepHoursAVG':
      return <FiMoon className={className} style={{ color }} />;
    case 'StepsAVG':
      return <TbWalk className={className} style={{ color }} />;
    case 'CaloriesAVG':
      return <FiZap className={className} style={{ color }} />;
    case 'GlucoseAVG':
      return <FiDroplet className={className} style={{ color }} />;
    case 'BloodPressureAVG':
      return <FiActivity className={className} style={{ color }} />;
    default:
      return null;
  }
};

const TeamHealthChart = ({ data = [] }) => {
  const [activeMetrics, setActiveMetrics] = useState({
    StressAvgAVG: true,
    HRVValueAvgAVG: true,
    SleepHoursAVG: true,
    StepsAVG: true,
    CaloriesAVG: true,
    GlucoseAVG: true,
    BloodPressureAVG: true,
  });

  const handleToggle = (key) => {
    setActiveMetrics(prev => {
      const activeCount = Object.values(prev).filter(Boolean).length;
      if (prev[key] && activeCount === 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const actualMaxes = useMemo(() => {
    const maxes = {};
    Object.keys(METRIC_CONFIGS).forEach(key => {
      const config = METRIC_CONFIGS[key];
      const vals = data.map(d => Number(d[key])).filter(v => !isNaN(v));
      const maxVal = vals.length > 0 ? Math.max(...vals) : config.max;
      maxes[key] = maxVal > config.max ? maxVal : config.max;
    });
    return maxes;
  }, [data]);

  const labels = useMemo(() => {
    return data.map(d => moment(d.date).format('D MMM'));
  }, [data]);

  const chartData = useMemo(() => {
    const datasets = Object.keys(METRIC_CONFIGS)
      .filter(key => activeMetrics[key])
      .map(key => {
        const config = METRIC_CONFIGS[key];
        const normalizedData = data.map(d => {
          const val = d[key];
          return getNormalizedValue(val, config, actualMaxes[key]);
        });

        const validPointsCount = normalizedData.filter(v => v !== null).length;

        return {
          label: config.label,
          data: normalizedData,
          fill: false,
          backgroundColor: config.chartColor,
          borderColor: config.chartColor,
          borderWidth: 2,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: config.chartColor,
          pointBorderWidth: 1.5,
          pointRadius: validPointsCount === 1 ? 4 : 0,
          pointHoverRadius: 5,
          lineTension: 0.4,
        };
      });

    return {
      labels,
      datasets
    };
  }, [data, activeMetrics, actualMaxes, labels]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: (typeof window !== 'undefined' ? window.devicePixelRatio : 2),
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
              color: 'rgba(241, 245, 249, 1)',
            },
            ticks: {
              fontFamily: 'Satoshi, sans-serif',
              fontColor: '#727272',
              fontSize: 11,
              maxTicksLimit: 12,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              color: 'rgba(241, 245, 249, 1)',
              drawBorder: false,
            },
            ticks: {
              min: 0,
              max: 100,
              fontFamily: 'Satoshi, sans-serif',
              fontColor: '#727272',
              fontSize: 11,
              stepSize: 20,
              callback: (value) => `${value}%`,
            },
          },
        ],
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#1D1F3F',
        titleFontFamily: 'Satoshi, sans-serif',
        titleFontSize: 13,
        titleFontColor: '#FFFFFF',
        bodyFontFamily: 'Satoshi, sans-serif',
        bodyFontSize: 12,
        bodyFontColor: '#FFFFFF',
        borderColor: '#374151',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (tooltipItem, chartData) => {
            const datasetIndex = tooltipItem.datasetIndex;
            const index = tooltipItem.index;
            const datasetLabel = chartData.datasets[datasetIndex].label;
            const normalizedVal = tooltipItem.yLabel;
            
            const metricKey = Object.keys(METRIC_CONFIGS).find(
              key => METRIC_CONFIGS[key].label === datasetLabel
            );
            
            if (metricKey) {
              const rawRecord = data[index];
              const rawVal = rawRecord ? rawRecord[metricKey] : null;
              if (rawVal !== null && rawVal !== undefined) {
                const formatted = formatValue(Number(rawVal), metricKey);
                const unit = METRIC_CONFIGS[metricKey].unit;
                return ` ${datasetLabel}: ${formatted}${unit} (${normalizedVal}%)`;
              }
            }
            return ` ${datasetLabel}: ${normalizedVal}%`;
          }
        }
      }
    };
  }, [data]);

  const kpis = useMemo(() => {
    return Object.keys(METRIC_CONFIGS).map(key => {
      const config = METRIC_CONFIGS[key];
      const result = calculateKPIs(data, key, config);
      return {
        key,
        ...config,
        ...result
      };
    });
  }, [data]);

  const latestRecord = useMemo(() => {
    if (!data || data.length === 0) return {};
    const sorted = [...data].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
    return sorted[0] || {};
  }, [data]);

  const comparisonPeriodLabel = useMemo(() => {
    if (!data || data.length < 2) return "";
    
    const mid = Math.ceil(data.length / 2);
    const firstRecord = data[6];
    const lastRecordOfFirstHalf = data[7];
    
    if (!firstRecord?.date || !lastRecordOfFirstHalf?.date) return "";
    
    const startStr = moment(firstRecord.date).format('D');
    const endStr = moment(lastRecordOfFirstHalf.date).format('D MMM');
    
    return `${startStr} vs ${endStr}`;
  }, [data]);

  const hasData = data && data.length > 0;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h4 className="text-lg font-bold text-brand-navy tracking-tight font-display">
            Métricas de Salud y Bienestar del Equipo
          </h4>
          <p className="text-xs font-semibold text-brand-gray-dark uppercase tracking-wider mt-1">
            Evolución y comparación de tendencias
          </p>
        </div>
      </div>

      {/* Interactive Toggle Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.keys(METRIC_CONFIGS).map(key => {
          const config = METRIC_CONFIGS[key];
          const isActive = activeMetrics[key];
          return (
            <button
              key={key}
              onClick={() => handleToggle(key)}
              style={{
                borderColor: isActive ? config.color : '#E2E8F0',
                backgroundColor: isActive ? config.bgColor : '#FFFFFF',
                color: isActive ? config.color : '#64748B',
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-semibold transition-all duration-200 hover:shadow-sm cursor-pointer"
            >
              <span 
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>

      {/* Line Chart */}
      <div className="relative h-[250px] w-full mb-6">
        {hasData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
            <FiInfo className="w-8 h-8 mb-2" />
            <span className="text-sm font-semibold">No hay datos disponibles para este período</span>
          </div>
        )}
      </div>

      {/* Bottom KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {kpis.map(kpi => {
          const rawVal = latestRecord[kpi.key] !== undefined && latestRecord[kpi.key] !== null
            ? Number(latestRecord[kpi.key])
            : (kpi.key === 'StressAvgAVG' ? 52
               : kpi.key === 'StepsAVG' ? 8200
               : kpi.key === 'SleepHoursAVG' ? 5.4
               : kpi.key === 'HRVValueAvgAVG' ? 65
               : 250);

          let displayVal = "0.00";
          let displayUnit = kpi.unit;

          if (kpi.key === 'SleepHoursAVG') {
            const sleepHours = Math.floor(rawVal);
            let sleepMinutes = Math.round((rawVal - sleepHours) * 60);
            let finalHours = sleepHours;
            if (sleepMinutes === 60) {
              finalHours += 1;
              sleepMinutes = 0;
            }
            displayVal = finalHours.toString();
            displayUnit = `h ${sleepMinutes}m`;
          } else {
            displayVal = rawVal.toFixed(2);
            displayUnit = kpi.key === 'StressAvgAVG' ? '/100' : kpi.unit.trim();
          }
          
          let trendBadgeClass = "px-2 py-0.5 text-[10px] font-bold rounded-lg border flex items-center gap-0.5 ";
          let trendIcon = null;

          if (kpi.trendDirection === 'better') {
            trendBadgeClass += "bg-emerald-50 text-emerald-700 border-emerald-200";
            trendIcon = kpi.trend > 0 ? <FiTrendingUp className="w-3 h-3" /> : <FiTrendingDown className="w-3 h-3" />;
          } else if (kpi.trendDirection === 'worse') {
            trendBadgeClass += "bg-rose-50 text-rose-700 border-rose-200";
            trendIcon = kpi.trend > 0 ? <FiTrendingUp className="w-3 h-3" /> : <FiTrendingDown className="w-3 h-3" />;
          } else {
            trendBadgeClass += "bg-slate-50 text-slate-500 border-slate-200";
          }

          const trendText = kpi.trend !== 0 
            ? `${kpi.trend > 0 ? '+' : ''}${kpi.trend.toFixed(1)}%`
            : '0.0%';

          return (
            <div key={kpi.key} className="bg-slate-50/40 border border-slate-100/70 rounded-xl p-3 flex items-center justify-between gap-2 hover:border-slate-200 transition-all duration-200">
              <div className="flex items-center gap-2.5 min-w-0">
                {/* Left: Icon Container */}
                <div className="p-1.5 rounded-lg bg-white shadow-sm border border-slate-100 flex-shrink-0 flex items-center justify-center">
                  {getIcon(kpi.key, kpi.color)}
                </div>
                
                {/* Middle: Label and Value */}
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate font-display">
                    {kpi.label}
                  </span>
                  <div className="flex items-baseline gap-0.5 mt-0.5 font-display truncate">
                    <span className="text-[15px] font-extrabold text-slate-900 tracking-tight">
                      {displayVal}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider ml-0.5">
                      {displayUnit}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Trend Badge & Comparison Date */}
              {hasData && (
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {comparisonPeriodLabel && (
                    <span className="text-[9px] font-medium text-slate-400 whitespace-nowrap tracking-tight">
                      {comparisonPeriodLabel}
                    </span>
                  )}
                  <span className={trendBadgeClass}>
                    {trendIcon}
                    <span className="ml-0.5">{trendText}</span>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(TeamHealthChart);
